import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { 
  LogOut, Clock, CheckCircle, XCircle, Loader2, Plus,
  FileText, Calendar, Building, Mail, GalleryHorizontalEnd, LayoutDashboard, Store, Sun, Moon
} from "lucide-react";
import { useTheme } from "../ThemeContext";
import AdGallery from "./AdGallery";
import MarketplaceView from "./tabs/marketplace/MarketplaceView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INGENIUM_LOGO = "https://customer-assets.emergentagent.com/job_agentic-curation/artifacts/zri9wsj7_ingenium-logo.png";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
  approved: { label: "Approved", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-500/20 text-red-400", icon: XCircle },
  delivered: { label: "Delivered", color: "bg-blue-500/20 text-blue-400", icon: CheckCircle }
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { light, toggle: toggleTheme } = useTheme();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState("");
  const [activeView, setActiveView] = useState("deals");
  const [hasSharedAds, setHasSharedAds] = useState(false);

  const token = localStorage.getItem("clientToken");

  const fetchDeals = useCallback(async () => {
    if (!token) {
      navigate("/?login=true");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/deals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeals(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("clientToken");
        localStorage.removeItem("clientEmail");
        localStorage.removeItem("clientName");
        localStorage.removeItem("clientUserId");
        navigate("/?login=true");
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to fetch deals");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    const name = localStorage.getItem("clientName");
    if (name) setClientName(name);
    fetchDeals();
    // Check if client has shared ads
    if (token) {
      axios.get(`${API}/client/shared-ads`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setHasSharedAds((res.data.ad_urls || []).length > 0))
        .catch(() => {});
    }
  }, [fetchDeals, token]);

  const handleLogout = () => {
    localStorage.removeItem("clientToken");
    localStorage.removeItem("clientEmail");
    localStorage.removeItem("clientName");
    localStorage.removeItem("clientUserId");
    navigate("/?login=true");
    toast.success("Logged out successfully");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border-0 flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const stats = {
    total: deals.length,
    pending: deals.filter(d => d.status === 'pending').length,
    approved: deals.filter(d => d.status === 'approved').length,
    delivered: deals.filter(d => d.status === 'delivered').length
  };

  return (
    <div className="min-h-screen bg-[#191b1d]" data-testid="client-dashboard">
      {/* Header */}
      <header className="navbar px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={INGENIUM_LOGO} alt="Ingenium Digital" className="h-8" />
          </Link>
          <div className="flex items-center gap-4">
            <button onClick={toggleTheme} className="p-2 rounded-lg text-[#ebebeb]/50 hover:text-white transition-colors" title={light ? "Dark mode" : "Light mode"} data-testid="client-theme-toggle">
              {light ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>
            <span className="text-[#ebebeb]/60 font-mono text-sm hidden sm:block">
              Welcome, {clientName}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#ebebeb]/60 hover:text-white transition-colors font-mono text-sm"
              data-testid="client-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* View Toggle */}
        <div className="flex items-center gap-2 mb-8">
          <button
            onClick={() => setActiveView("deals")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-mono text-sm transition-colors ${
              activeView === "deals" ? "bg-[#ff3737]/10 text-[#ff3737]" : "text-[#ebebeb]/50 hover:bg-white/5 hover:text-[#ebebeb]"
            }`}
            data-testid="client-deals-tab"
          >
            <LayoutDashboard className="w-4 h-4" />
            My Deals
          </button>
          {hasSharedAds && (
            <button
              onClick={() => setActiveView("gallery")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-mono text-sm transition-colors ${
                activeView === "gallery" ? "bg-[#ff3737]/10 text-[#ff3737]" : "text-[#ebebeb]/50 hover:bg-white/5 hover:text-[#ebebeb]"
              }`}
              data-testid="client-gallery-tab"
            >
              <GalleryHorizontalEnd className="w-4 h-4" />
              Ad Gallery
            </button>
          )}
          <button
            onClick={() => setActiveView("marketplace")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-mono text-sm transition-colors ${
              activeView === "marketplace" ? "bg-[#ff3737]/10 text-[#ff3737]" : "text-[#ebebeb]/50 hover:bg-white/5 hover:text-[#ebebeb]"
            }`}
            data-testid="client-marketplace-tab"
          >
            <Store className="w-4 h-4" />
            Marketplace
          </button>
        </div>

        {activeView === "gallery" ? (
          <AdGallery mode="client" clientToken={token} />
        ) : activeView === "marketplace" ? (
          <MarketplaceView token={token} requestDelete={() => {}} isSuperAdmin={false} />
        ) : (
        <>
        {/* Title & Action */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white font-mono">
              My Deals
            </h1>
            <p className="text-[#ebebeb]/60 mt-1 font-mono text-sm">
              Track your deal requests and their status
            </p>
          </div>
          <Link to="/?create=true">
            <button className="ingenium-button" data-testid="new-deal-btn">
              <span className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                New Deal Request
              </span>
            </button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="feature-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#ebebeb]/60 font-mono">Total Deals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white font-mono">{stats.total}</div>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#ebebeb]/60 font-mono flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-400" /> Pending
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-400 font-mono">{stats.pending}</div>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#ebebeb]/60 font-mono flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" /> Approved
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-400 font-mono">{stats.approved}</div>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#ebebeb]/60 font-mono flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-400" /> Delivered
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-400 font-mono">{stats.delivered}</div>
            </CardContent>
          </Card>
        </div>

        {/* Deals List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#ff3737]" />
          </div>
        ) : deals.length === 0 ? (
          <Card className="feature-card">
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 text-[#ebebeb]/30 mx-auto mb-4" />
              <h3 className="text-white font-mono text-lg mb-2">No deals yet</h3>
              <p className="text-[#ebebeb]/60 font-mono text-sm mb-6">
                Create your first deal request to get started
              </p>
              <Link to="/?create=true">
                <button className="ingenium-button">
                  <span className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Create a Deal
                  </span>
                </button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {deals.map((deal) => (
              <Card key={deal.id} className="feature-card" data-testid={`deal-card-${deal.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-white font-mono font-bold">{deal.dsp}</h3>
                        {getStatusBadge(deal.status)}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-[#ebebeb]/60">
                          <Building className="w-4 h-4" />
                          <span className="font-mono">{deal.company}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#ebebeb]/60">
                          <Mail className="w-4 h-4" />
                          <span className="font-mono">{deal.dsp_seat_number}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#ebebeb]/60">
                          <Calendar className="w-4 h-4" />
                          <span className="font-mono">{formatDate(deal.created_at)}</span>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {deal.media_type.map((type) => (
                          <Badge key={type} variant="outline" className="border-[#ff3737]/30 text-[#ff3737] text-xs font-mono">
                            {type}
                          </Badge>
                        ))}
                        {deal.device.map((device) => (
                          <Badge key={device} variant="outline" className="border-white/20 text-[#ebebeb]/60 text-xs font-mono">
                            {device}
                          </Badge>
                        ))}
                      </div>

                      {/* Primary KPI */}
                      {deal.primary_kpi && (
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-[#ebebeb]/40 text-xs font-mono uppercase">KPI:</span>
                          <Badge className="bg-[#ff3737]/10 text-[#ff3737] border-0 text-xs font-mono">
                            {deal.primary_kpi}
                            {deal.primary_kpi_value && (
                              <span className="ml-1.5 font-semibold">
                                {["CPM","CPC","CPCV"].includes(deal.primary_kpi)
                                  ? `${deal.primary_kpi_currency || "USD"} ${deal.primary_kpi_value}`
                                  : `${deal.primary_kpi_value}%`}
                              </span>
                            )}
                          </Badge>
                        </div>
                      )}
                      
                      {/* SSP Deal IDs - Read Only */}
                      {deal.ssp_deals && deal.ssp_deals.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <p className="text-[#ebebeb]/40 text-xs uppercase tracking-wide mb-2 font-mono">SSP Deal IDs</p>
                          <div className="space-y-2">
                            {deal.ssp_deals.map((entry, index) => (
                              <div key={index} className="bg-white/5 rounded-lg px-3 py-2 space-y-1">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-purple-500/20 text-purple-400 border-0 font-mono text-xs">
                                    {entry.ssp}
                                  </Badge>
                                  <span className="text-white font-mono text-sm">{entry.deal_id}</span>
                                </div>
                                {entry.deal_name && (
                                  <p className="text-[#ebebeb]/40 text-xs font-mono pl-1">{entry.deal_name}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-[#ebebeb]/40 text-xs font-mono">
                        {deal.geography.length} {deal.geography.length === 1 ? 'country' : 'countries'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
};

export default ClientDashboard;
